import { createFlagsmithInstance, IFlagsmith } from 'flagsmith';

/**
 * Service for managing Flagsmith feature flags
 * Provides centralized methods for flag operations
 */
class FeatureFlagService {
    private static instance: IFlagsmith;

    /**
     * Get or create the Flagsmith instance (lazy initialization)
     */
    get flagsmith(): IFlagsmith {
        if (!FeatureFlagService.instance) {
            FeatureFlagService.instance = createFlagsmithInstance();
        }
        return FeatureFlagService.instance;
    }

    /**
     * Check if a feature flag is enabled
     * @param flagKey - The feature flag key
     * @returns true if the flag is enabled, false otherwise
     */
    isFeatureEnabled(flagKey: string): boolean {
        try {
            return this.flagsmith.hasFeature(flagKey);
        } catch (error) {
            console.warn(`[FeatureFlagService] Error checking flag "${flagKey}":`, error);
            return false;
        }
    }

    /**
     * Get the value of a feature flag
     * @param defaultValue - Default value to return if flag doesn't exist
     * @returns The flag value or default value
     */
    getFlagValue<T = string>(flagKey: string, defaultValue?: T): T | undefined {
        try {
            const value = this.flagsmith.getValue(flagKey, {
                json: true
            });
            return (value as T) ?? defaultValue;
        } catch (error) {
            console.warn(`[FeatureFlagService] Error getting flag value "${flagKey}":`, error);
            return defaultValue;
        }
    }

    /**
     * Log flags status for debugging (development only)
     * @param flagKeys - Specific keys to log (optional)
     */
    logFlagsStatus(flagKeys?: string[]): void {
        if (process.env.NODE_ENV !== 'development') return;

        const flags = this.flagsmith.getAllFlags();
        const keysToLog = flagKeys || Object.keys(flags || {});

        console.info('[FeatureFlagService] Feature flags status:');
        keysToLog.forEach((key) => {
            const enabled = this.flagsmith.hasFeature(key);
            const value = this.flagsmith.getValue(key, { json: true });
            console.info(`${enabled ? '🟢' : '❌'} ${String(key)}:`, { enabled, value });
        });
    }
}

const flagsmithService = new FeatureFlagService();
export { flagsmithService as FeatureFlagService };
